import { chain, mapValues, omit, pick, pickBy } from "lodash";
import { Clipper, JoinType } from "../clipper";
import { ClipperShape } from "../clipper/ClipperShape";
import { mat3 } from "../common";
import { PcbNodeHelper } from "../nodes";
import { TraceCorners } from "../pcb/libs/traceCorners";
import { FillCutoutType, fillGetKeepoutMode, getFillCutoutShape, getThermalReliefCutoutShape } from "./FillHelpers";
import { calcKeepoutForNodes, canNodeHaveKeepout } from "./keepOutHelpers";
import { createThermalReliefConductorPolygons, isThermalReliefRequired } from "./ThermalReliefs";
export const CurveDivisions = 24; // 24 divisions in a circle gives visually smooth curves for typical pcb-sized objects up to 3:1 zoom, and workable beyond 30:1 zoom
export const CurveDivisionsForExport = CurveDivisions * 1; // Higher resolution for export, to avoid export artifacts
/** Manage the current fill shapes, and incremental updates, for each net and layer */
export class CopperFillHandler {
    layer;
    logger;
    // The static below is a global cache of previous layer shape data to speed up generating
    // new shapes. The cache for a given net/layer is created when first requested for that combo.
    // TODO these cache entries currently live for the entire app lifecycle. This is
    // not a massive problem, because the set of layers tends to be a) small, and b)
    // infrequently changed (usually unchanged for the duration of an edit session).
    // An new edit session clears the cache, and there's no known problems associated
    // with keeping previous layer data in memory in the meantime, and the extra
    // management necessary to keep it pure seems needlessly complex. However, this
    // may be a candidate for improving in future.
    static handlersByNodeLayer = {};
    lastBaseCutout;
    lastKeepOutShapes = {};
    lastThermalConductors = {};
    lastThermalCutout = {};
    // Don't call the constructor directly; instead call getHandlerForNodeLayer()
    constructor(layer, logger) {
        this.layer = layer;
        this.logger = logger;
        this.lastBaseCutout = Clipper.shape([]);
    }
    /** Return the global fill handler for the given net/layer, creating it if it doesn't exist. */
    static getHandlerForNodeLayer(layer, nodeId, logger) {
        if (!(nodeId in CopperFillHandler.handlersByNodeLayer)) {
            CopperFillHandler.handlersByNodeLayer[nodeId] = {};
        }
        const netHandler = CopperFillHandler.handlersByNodeLayer[nodeId];
        if (!netHandler) {
            throw new Error(`Expected valid fill handler for net/polygon ${nodeId}, but found none`);
        }
        if (!(layer.uid in netHandler)) {
            netHandler[layer.uid] = new CopperFillHandler(layer, logger);
        }
        const layerHandler = netHandler[layer.uid];
        if (!layerHandler) {
            throw new Error(`Expected valid layer handler for net/polygon ${nodeId} and layer ${layer.uid}, but found none`);
        }
        return layerHandler;
    }
    /**
     * Use resetCache when deleting nodes. In that case, updatedNodes should be
     * all nodes.
     */
    getUpdatedFillShape(updatedNodes, context, baseShape, baseShapeMask, accumulatorMask, layoutSpacing, parentNode, netUid, minTraceWidth = 0, resetCache = false) {
        if (resetCache) {
            this.lastBaseCutout = Clipper.shape([]);
            this.lastKeepOutShapes = {};
            this.lastThermalConductors = {};
            this.lastThermalCutout = {};
        }
        // ============================================================
        // Prepare the starting shape
        // baseShape (layout) -> offset in (avoid layout)
        // baseShapeMask (polygon)
        // accumulatorMask (other fills to avoid) already offsetted by keepout
        // baseShapeMasked (final) = ((baseShape - accumulatorMask) & baseShapeMask) offsetted by -minTraceWidth
        const baseShapeClipper = Clipper.shape([baseShape.getPoints(CurveDivisions)]).offset(-layoutSpacing);
        const baseShapeMaskClipper = baseShapeMask ? Clipper.shape([baseShapeMask.getPoints(CurveDivisions)]) : null;
        // TODO: update to avoid mat3 -> THREE conversion. Should be possible now
        baseShapeMaskClipper?.transform3Shape(mat3.toThreeMatrix3(parentNode.bakedRules.transformRelativeToShapesLayer));
        const baseShapeClipperWithoutAccumulator = accumulatorMask
            ? baseShapeClipper.subtract([accumulatorMask])
            : baseShapeClipper;
        const baseShapeMasked = (baseShapeMaskClipper
            ? baseShapeClipperWithoutAccumulator.intersect(baseShapeMaskClipper)
            : baseShapeClipperWithoutAccumulator)
            // We offset by -minTraceWidth since we then expand it back later, to remove small copper features
            .offset(-minTraceWidth, {
            joinType: JoinType.Square,
        });
        // ============================================================
        // Cleanup cache if needed
        // eslint-disable-next-line @buildwithflux/no-array-foreach
        Object.values(updatedNodes).forEach((updatedNode) => {
            // If the nodes being updated were previously assigned to this layer, then we need to do some
            // clean up to remove thermal relief geometry, for those nodes, from this fill. That's what this
            // logic is doing - it aggressively cleans the 'last shape' records, for each update node, any time
            // a layer mismatch is detected
            if (!PcbNodeHelper.isNodeConnectedToThisLayer(updatedNode, this.layer)) {
                delete this.lastThermalConductors[updatedNode.uid];
                delete this.lastThermalCutout[updatedNode.uid];
            }
            // Important clean up for case where update node previously had thermal relief but now no longer does.
            // In this scenario, cached records for the node are likely to exist - which need to be removed. Doing
            // so ensures changes from thermal relief to direct connect, yield expected cutout update for that node
            // on fill geometry
            if (!PcbNodeHelper.nodeHasThermalRelief(updatedNode, netUid, this.layer) &&
                this.lastThermalConductors[updatedNode.uid]) {
                delete this.lastThermalConductors[updatedNode.uid];
                delete this.lastThermalCutout[updatedNode.uid];
            }
        });
        // ============================================================
        // Prepare and cut the keepout shapes
        const nonThermalCutoutNodes = pickBy(updatedNodes, (updatedNode) => !PcbNodeHelper.nodeHasThermalRelief(updatedNode, netUid, this.layer));
        const thermalCutoutNodes = pickBy(updatedNodes, (updatedNode) => PcbNodeHelper.nodeHasThermalRelief(updatedNode, netUid, this.layer));
        this.lastThermalConductors = chain(thermalCutoutNodes)
            .reduce((lastThermalConductors, updatedNode) => {
            if (isThermalReliefRequired(updatedNode)) {
                const keepOutWidth = canNodeHaveKeepout(updatedNode)
                    ? calcKeepoutForNodes(updatedNode, parentNode, context.pcbLayoutSelectorMatching)
                    : 0;
                const thermalConductorPolygons = createThermalReliefConductorPolygons(updatedNode.bakedRules, keepOutWidth);
                const thermalConductorShapes = thermalConductorPolygons.map((i) => Clipper.shape([i], true));
                lastThermalConductors[updatedNode.uid] = thermalConductorShapes;
            }
            return lastThermalConductors;
        }, this.lastThermalConductors)
            .value();
        const fillCutouts = this.getKeepOutShapes(parentNode, nonThermalCutoutNodes, netUid, context);
        const baseCutout = this.getNextBaseCutout(fillCutouts);
        // We already offset baseShapeMasked by -minTraceWidth to apply small-feature-removal to the
        // layout shape. Here we offset the cutout by the same amount (negated because we're applying it
        // to the cutout, not the positive shape). Below we will undo both offsets by the same amount on
        // the final shape, resulting in an identical shape but without small features.
        const offsetCutout = baseCutout.offset(minTraceWidth, {
            joinType: JoinType.Square,
        });
        this.lastThermalCutout = chain(thermalCutoutNodes)
            .reduce((output, node) => {
            const shape = getThermalReliefCutoutShape(node, parentNode, CurveDivisions, context);
            if (shape) {
                output[node.uid] = shape;
            }
            else {
                delete output[node.uid];
            }
            return output;
        }, this.lastThermalCutout)
            .value();
        const fillShapeMinusNonThermalReliefCutouts = baseShapeMasked.subtract([offsetCutout]);
        // Here we reverse the min-feature-size offset applied to baseCutout and baseShapeMasked above,
        // resulting in the desired shape but without small features
        const fillShapeMinusAllCutouts = fillShapeMinusNonThermalReliefCutouts
            .subtract(Object.values(this.lastThermalCutout))
            .offset(minTraceWidth, {
            joinType: JoinType.Square,
        });
        // Build combined union of all thermal cutout records
        const thermalCutoutUnion = Clipper.shape([]).union(Object.values(this.lastThermalCutout));
        // TODO is there a way to do the intersects below in bulk? This is a prime candidate for really slow behaviour
        const trimmedConductorShapes = Object.values(this.lastThermalConductors)
            .flat()
            .map((conductorShape) => {
            // For a given thermal conductor, perform the intersection if it with (the combination of) thermal relief cutouts. This
            // constrains the geometry of conductor shapes (which are essentially long rectangles), to the boundary of thermal relief
            // cutout, which - for the most part - prevents thermal conductor geometry from overlapping cutout regions in other areas
            // of the board
            return conductorShape.intersect(thermalCutoutUnion);
        })
            .filter((constrainedConductorShape) => {
            const isIntersected = ClipperShape.shapesIntersect(constrainedConductorShape, offsetCutout);
            // If there is no intersection between this constrained conductor, and offset cutout, that implies the conductor is not
            // overlapping the keep out region of another non thermal relief feature, and we therefore include it in the geometry output
            return !isIntersected;
        });
        return fillShapeMinusAllCutouts.union(trimmedConductorShapes);
    }
    getKeepOutShapes(fillParentNode, pcbNodeSubset, netUid, context) {
        const pcbNodesWithCurveTraces = { ...pcbNodeSubset };
        // When getting keepout shapes (usually to update fill geometry), we need to ensure that,
        // for any route segments in the pcbNodes map, for any route segment with a curved corner,
        // the adjacent connected route segment (eg paired around that corner) is also included in
        // the keepout result. This ensures (updated) curve geometry from that other route segment
        // is included in the update to fill geometry
        for (const routeSegmentWithCurveShape of TraceCorners.connectedToCornerTracesGenerator(context.pcbNodes, context.pcbTracingGraph.getGraph())) {
            pcbNodesWithCurveTraces[routeSegmentWithCurveShape.uid] = routeSegmentWithCurveShape;
        }
        const nodeKeepOutModes = mapValues(pcbNodesWithCurveTraces, (node) => fillGetKeepoutMode(node, context.pcbNodes, this.layer, netUid));
        const keepoutShapes = chain(pcbNodesWithCurveTraces)
            .pickBy((node) => nodeKeepOutModes[node.uid] !== FillCutoutType.NoCutout &&
            // NOTE: Stitching vias are handled separately when desired
            // using getStitchingCutout.
            !PcbNodeHelper.isStitchingNode(node))
            .mapValues((bakedPcbNode) => {
            const shape = // NOTE: underlying createCircleOrOblongShape is globally cached
             getFillCutoutShape(bakedPcbNode, fillParentNode, nodeKeepOutModes[bakedPcbNode.uid] ?? FillCutoutType.NoCutout, CurveDivisions, context);
            if (!shape) {
                // @ts-expect-error: for logging only
                const size = bakedPcbNode.bakedRules.size;
                // QUESTION: is it expected to have nodes of zero size? and why?
                // eslint-disable-next-line no-console
                console.warn(`FLUX: getKeepOutShapes: shape is undefined for ${bakedPcbNode.name} ${bakedPcbNode.type} of size ${JSON.stringify(size)}`);
            }
            return shape;
        })
            .pickBy(Boolean)
            .value();
        return keepoutShapes;
    }
    getNextBaseCutout(nextKeepOutShapes) {
        // Get any previous keepouts belonging to the updated nodes
        const keys = Object.keys(nextKeepOutShapes);
        const staleKeepoutShapes = pick(this.lastKeepOutShapes, keys);
        const cutoutToFill = Clipper.shape([]).union(Object.values(staleKeepoutShapes));
        // Get any unchanged keepouts that may need repair because they overlap
        // with the updated nodes' old keepouts we're removing below.
        const otherKeepoutShapes = omit(this.lastKeepOutShapes, keys);
        // NOTE: shapeBounds is int space but it's fine here since we just compare it
        const bounds = cutoutToFill.shapeBounds();
        const repairKeepOutShapes = pickBy(otherKeepoutShapes, (s) => ClipperShape.boundsOverlap(s.shapeBounds(), bounds));
        // With the last cutout, fill in the old positions of the new keepouts
        // and then re-cut any overlapping old keepouts.
        const repairedCutout = this.lastBaseCutout.subtract([cutoutToFill]).union(Object.values(repairKeepOutShapes));
        // Cut the new keepouts of the updated nodes
        const baseCutout = repairedCutout.union(Object.values(nextKeepOutShapes));
        this.lastBaseCutout = baseCutout;
        this.lastKeepOutShapes = Object.assign(this.lastKeepOutShapes, nextKeepOutShapes);
        return baseCutout;
    }
}
//# sourceMappingURL=CopperFillHandler.js.map